import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = 'https://teamsol-api-serverless.suncropgroup.com.pk';
// const API_URL = 'http://localhost:3001';

// Odoo sync stream (SSE). The sync used to be a standalone Lambda behind its
// own Function URL; it now runs inside the main API on the same host, so this
// follows API_URL rather than being a separate origin.
// Backend route: suncrop-be src/routes/odoo-sync.ts
const STREAM_API_URL = `${API_URL}/odoo-sync`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// A helper function that wraps an API promise with toast notifications
const toastWrap = async <T>(
  promise: Promise<T>,
  {
    loading,
    success,
    error,
  }: {
    loading: string;
    success: string | ((res: T) => string);
    error: string | ((err: any) => string);
  },
): Promise<T> => {
  return toast.promise(promise, {
    loading,
    success: success as any,
    error: error as any,
  });
};

// =======================
// AUTH API
// =======================
export const authAPI = {
  login: async (email: string, password: string) => {
    return toastWrap(
      api.post('/admin/auth/login', { email, password }).then((response) => {
        if (response.data.data.token) {
          localStorage.setItem('token', response.data.data.token);
        }
        return response.data.data;
      }),
      {
        loading: 'Logging in...',
        success: 'Logged in successfully',
        error: (err: any) => err.response?.data?.message || 'Login failed',
      },
    );
  },

  logout: async () => {
    return toastWrap(
      api
        .post('/admin/auth/logout')
        .then((response) => response.data)
        .finally(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }),
      {
        loading: 'Logging out...',
        success: 'Logged out successfully',
        error: (err: any) => err.response?.data?.message || 'Logout failed',
      },
    );
  },

  verifyUser: async () => {
    return toastWrap(
      api.get('/admin/auth/verify').then((response) => response.data),
      {
        loading: 'Verifying user...',
        success: 'User verified',
        error: (err: any) =>
          err.response?.data?.message || 'User verification failed',
      },
    );
  },

  updatePassword: async (currentPassword: string, newPassword: string) => {
    return toastWrap(
      api
        .put('/admin/auth/update-password', { currentPassword, newPassword })
        .then((response) => response.data),
      {
        loading: 'Updating password...',
        success: 'Password updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update password',
      },
    );
  },

  forgotPassword: async (email: string) => {
    return toastWrap(
      api
        .post('/admin/auth/forgot-password', { email })
        .then((response) => response.data),
      {
        loading: 'Processing request...',
        success: 'Password reset email sent',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to process request',
      },
    );
  },

  resetPassword: async (token: string, password: string) => {
    return toastWrap(
      api
        .post('/admin/auth/reset-password', { token, password })
        .then((response) => response.data),
      {
        loading: 'Resetting password...',
        success: 'Password reset successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to reset password',
      },
    );
  },
};

// =======================
// PROJECTS API
// =======================

interface Project {
  _id: string;
  id: number;
  name: string;
  status: number;
  company_id: number | { id: number; name: string };
  logo?: string;
  color_scheme: {
    primary: string;
    secondary: string;
  };
  odoo: {
    url: string;
    db: string;
    username: string;
    password: string;
  };
  created_at: Date;
  updated_at: Date;
  admin_user_id: string | { name: string; email: string };
}

interface ProjectsResponse {
  projects: Project[];
  pagination: {
    total: number;
    page: number;
    pages: number;
  };
}

interface ApiResponse<T> {
  pagination: any;
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  sessionId?: string;
  // Cross-match count properties
  odooOnlyCount?: number;
  localOnlyCount?: number;
  totalCombined?: number;
}

export const projectAPI = {
  getAll: async (
    page: number = 1,
    limit: number = 10,
    options?: { search?: string; status?: string },
  ): Promise<ApiResponse<ProjectsResponse>> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.status === '0' || options?.status === '1') {
      params.set('status', options.status);
    }
    return toastWrap(
      api
        .get(`/admin/projects?${params.toString()}`)
        .then((response) => response.data),
      {
        loading: 'Fetching projects...',
        success: 'Projects fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch projects',
      },
    );
  },

  create: async (data: any) => {
    return toastWrap(
      api.post('/admin/projects', data).then((response) => response.data),
      {
        loading: 'Creating project...',
        success: 'Project created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create project',
      },
    );
  },

  update: async (id: number | string, data: any) => {
    return toastWrap(
      api.put(`/admin/projects/${id}`, data).then((response) => response.data),
      {
        loading: 'Updating project...',
        success: 'Project updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update project',
      },
    );
  },

  delete: async (id: string): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.delete(`/admin/projects/${id}`).then((response) => response.data),
      {
        loading: 'Deleting project...',
        success: 'Project deleted successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to delete project',
      },
    );
  },

  getById: async (id: string): Promise<ApiResponse<Project>> => {
    return toastWrap(
      api.get(`/admin/projects/${id}`).then((response) => response.data),
      {
        loading: 'Fetching project...',
        success: 'Project fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch project',
      },
    );
  },

  toggleStatus: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .patch(`/admin/projects/${id}/toggle-status`)
        .then((response) => response.data),
      {
        loading: 'Toggling project status...',
        success: 'Project status toggled successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to toggle project status',
      },
    );
  },

  authorizeOdoo: async (odooConfig: {
    odoo_url: string;
    odoo_db: string;
    odoo_username: string;
    odoo_password: string;
  }): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/projects/authorize-odoo', odooConfig)
        .then((response) => response.data),
      {
        loading: 'Authorizing Odoo...',
        success: 'Odoo authorized successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to authorize Odoo',
      },
    );
  },
};

export const salesAPI = {
  getAllSales: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`/sales/admin/?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Sales...',
        success: 'Sales fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Sales',
      },
    );
  },

  getSalesFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `/sales/filter/admin?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Sales...',
        success: 'Sales fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Sales',
      },
    );
  },
};

export const tollsAPI = {
  getAllTolls: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`fleet/toll/admin?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Toll Records...',
        success: 'Tolls record fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch tolls',
      },
    );
  },

  gettollsFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `/fleet/toll/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Tolls Records...',
        success: 'Tolls record fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch tolls',
      },
    );
  },
};

export const serviceAPI = {
  getAllServices: async (
    page: number = 1,
    limit: number = 50,
    type: 'service' | 'fuel',
    search?: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    const searchParam = search ? `&search=${search}` : '';
    return toastWrap(
      api
        .get(
          `fleet/service/admin?page=${page}&limit=${limit}&type=${type}${searchParam}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Service Records...',
        success: 'Service record fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch service',
      },
    );
  },

  getServicesFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
    type: 'service' | 'fuel',
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `fleet/service/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}&type=${type}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Service Records...',
        success: 'Service record fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch service',
      },
    );
  },

  crossMatchFuel: async (
    startDate: string,
    endDate: string,
    odoo_configuration_id: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get(
          `admin/cross-match/fuel?startDate=${startDate}&endDate=${endDate}&configId=${odoo_configuration_id}${
            sessionId ? `&sessionId=${sessionId}` : ''
          }`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Cross-match records...',
        success: 'Cross-match records fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch cross-match records',
      },
    );
  },

  crossMatchMaintenance: async (
    startDate: string,
    endDate: string,
    odoo_configuration_id: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get(
          `admin/cross-match/maintenance?startDate=${startDate}&endDate=${endDate}&configId=${odoo_configuration_id}${
            sessionId ? `&sessionId=${sessionId}` : ''
          }`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Maintenance records...',
        success: 'Maintenance records fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch maintenance records',
      },
    );
  },

  getOdooSession: async (configId: string): Promise<ApiResponse<any>> => {
    return api
      .get(`/web/authenticate-odoo-server?id=${configId}`)
      .then((response) => response.data);
  },

  syncServiceToOdoo: async (
    recordId: number | string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('admin/cross-match/sync-to-odoo/service', {
          recordId,
          configId,
          sessionId,
        })
        .then((response) => response.data),
      {
        loading: 'Syncing to Odoo...',
        success: 'Successfully synced to Odoo',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to sync to Odoo',
      },
    );
  },

  syncServiceToLocal: async (odooRecord: any): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('admin/cross-match/sync-to-local/service', { odooRecord })
        .then((response) => response.data),
      {
        loading: 'Creating local record...',
        success: 'Successfully created local record',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create local record',
      },
    );
  },

  crossMatchToll: async (
    fromDate: string,
    toDate: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get('admin/cross-match/toll', {
          params: { startDate: fromDate, endDate: toDate, configId, sessionId },
        })
        .then((response) => response.data),
      {
        loading: 'Processing toll cross-match...',
        success: 'Toll cross-match completed',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to process cross-match',
      },
    );
  },

  syncTollToOdoo: async (
    recordId: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('admin/cross-match/sync-to-odoo/toll', {
          recordId,
          configId,
          sessionId,
        })
        .then((response) => response.data),
      {
        loading: 'Syncing toll to Odoo...',
        success: 'Successfully synced to Odoo',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to sync to Odoo',
      },
    );
  },

  syncTollToLocal: async (odooRecord: any): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('admin/cross-match/sync-to-local/toll', { odooRecord })
        .then((response) => response.data),
      {
        loading: 'Creating local toll record...',
        success: 'Successfully created local toll record',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create local toll record',
      },
    );
  },

  crossMatchPersonalUse: async (
    startDate: string,
    endDate: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get(
          `/admin/cross-match/personal-use?startDate=${startDate}&endDate=${endDate}&configId=${configId}&sessionId=${sessionId}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching cross-match records...',
        success: 'Records fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch records',
      },
    );
  },

  syncPersonalUseToLocal: async (
    odooRecord: any,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/cross-match/sync-to-local/personal-use', { odooRecord })
        .then((response) => response.data),
      {
        loading: 'Creating local record...',
        success: 'Record created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create record',
      },
    );
  },

  crossMatchExpenses: async (
    startDate: string,
    endDate: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get(
          `/admin/cross-match/expense?startDate=${startDate}&endDate=${endDate}&configId=${configId}&sessionId=${sessionId}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching expense cross-match records...',
        success: 'Records fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch records',
      },
    );
  },

  syncExpenseToOdoo: async (
    recordId: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/cross-match/sync-to-odoo/expense', {
          recordId,
          configId,
          sessionId,
        })
        .then((response) => response.data),
      {
        loading: 'Syncing to Odoo...',
        success: 'Successfully synced to Odoo',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to sync to Odoo',
      },
    );
  },

  syncExpenseToLocal: async (odooRecord: any): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/cross-match/sync-to-local/expense', { odooRecord })
        .then((response) => response.data),
      {
        loading: 'Creating local expense record...',
        success: 'Record created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create record',
      },
    );
  },

  syncPersonalUseToOdoo: async (
    recordId: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/cross-match/sync-to-odoo/personal-use', {
          recordId,
          configId,
          sessionId,
        })
        .then((response) => response.data),
      {
        loading: 'Syncing to Odoo...',
        success: 'Synced successfully',
        error: (err: any) => err.response?.data?.message || 'Sync failed',
      },
    );
  },

  // Daily Activity Cross-Match
  crossMatchDailyActivity: async (
    startDate: string,
    endDate: string,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get(
          `/admin/cross-match/daily-activity?startDate=${startDate}&endDate=${endDate}&configId=${configId}&sessionId=${sessionId}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching daily activity cross-match records...',
        success: 'Records fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch records',
      },
    );
  },

  syncDailyActivityToOdoo: async (
    dailyActivityId: number,
    configId: string,
    sessionId?: string,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/cross-match/sync-to-odoo/daily-activity', {
          dailyActivityId,
          configId,
          sessionId,
        })
        .then((response) => response.data),
      {
        loading: 'Syncing to Odoo...',
        success: 'Successfully synced to Odoo',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to sync to Odoo',
      },
    );
  },

  syncDailyActivityToLocal: async (
    odooRecord: any,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/cross-match/sync-to-local/daily-activity', { odooRecord })
        .then((response) => response.data),
      {
        loading: 'Creating local daily activity record...',
        success: 'Record created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create record',
      },
    );
  },
};

export const PersonalUseAPI = {
  getAllPersonalUse: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`fleet/personal-use/admin?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Personal Use Records...',
        success: 'Personal use record fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch personal use',
      },
    );
  },

  getPersonalUseAPIFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `/fleet/personal-use/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Personal-use...',
        success: 'Personal-ise fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Personal-use',
      },
    );
  },
};

export const dailyActivityAPI = {
  getAllDailyActivities: async (
    page: number = 1,
    limit: number = 50,
    search?: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    const searchParam = search ? `&search=${search}` : '';
    return toastWrap(
      api
        .get(`daily-activities/admin?page=${page}&limit=${limit}${searchParam}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Daily Activities...',
        success: 'Daily Activities fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch daily activities',
      },
    );
  },

  getDailyActivitiesFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `daily-activities/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Daily Activities...',
        success: 'Daily Activities fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch daily activities',
      },
    );
  },
};

export const CreditLimitApplyAPI = {
  getAllCreditLimitApply: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`credit-limit/apply/admin?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Apply...',
        success: 'Credit Limit Apply fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Credit Limit Apply',
      },
    );
  },

  getCreditLimitApplyAPIFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `/credit-limit/apply/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Apply...',
        success: 'Credit Limit Apply fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Credit Limit Apply',
      },
    );
  },
};

export const CreditLimitExtensionAPI = {
  getAllCreditLimitExtension: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`credit-limit/extension/admin?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Extension...',
        success: 'Credit Limit Extension fetched successfully',
        error: (err: any) =>
          err.response?.data?.message ||
          'Failed to fetch Credit Limit Extension',
      },
    );
  },

  getCreditLimitExtensionAPIFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `credit-limit/extension/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Extension...',
        success: 'Credit Limit Extension fetched successfully',
        error: (err: any) =>
          err.response?.data?.message ||
          'Failed to fetch Credit Limit Extension',
      },
    );
  },
};

export const temporaryCreditAPI = {
  getAllTemporaryCredits: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`credit-limit/temporary/admin?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Extension...',
        success: 'Credit Limit Extension fetched successfully',
        error: (err: any) =>
          err.response?.data?.message ||
          'Failed to fetch Credit Limit Extension',
      },
    );
  },

  getTemporaryCreditsFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `credit-limit/temporary/admin/filter?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Extension...',
        success: 'Credit Limit Extension fetched successfully',
        error: (err: any) =>
          err.response?.data?.message ||
          'Failed to fetch Credit Limit Extension',
      },
    );
  },
};

export const expenseAPI = {
  getExpenses: async (
    page: number = 1,
    limit: number = 50,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(`expense/admin?page=${page}&limit=${limit}`)
        .then((response) => response.data),
      {
        loading: 'Fetching Expenses...',
        success: 'Expenses fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Expenses',
      },
    );
  },

  getExpensesFilter: async (
    page: number = 1,
    limit: number = 50,
    startDate?: string,
    endDate?: string,
  ): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api
        .get(
          `expense/admin?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        )
        .then((response) => response.data),
      {
        loading: 'Fetching Credit Limit Extension...',
        success: 'Credit Limit Extension fetched successfully',
        error: (err: any) =>
          err.response?.data?.message ||
          'Failed to fetch Credit Limit Extension',
      },
    );
  },
};

// =======================
// COMPANIES API
// =======================
export const companiesAPI = {
  getAll: async (odooId?: number): Promise<ApiResponse<any>> => {
    const url = odooId ? `/companies?odoo_id=${odooId}` : '/companies';
    return toastWrap(
      api.get(url).then((response) => response.data),
      {
        loading: 'Fetching companies...',
        success: 'Companies fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch companies',
      },
    );
  },
};

// =======================
// EMPLOYEE API
// =======================
export const employeeAPI = {
  getAll: async (
    page: number = 1,
    limit: number = 100,
    options?: { search?: string; status?: string },
  ): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.status === '0' || options?.status === '1') {
      params.set('status', options.status);
    }
    return toastWrap(
      api
        .get(`/employee?${params.toString()}`)
        .then((response) => response.data),
      {
        loading: 'Fetching employees...',
        success: 'Employees fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch employees',
      },
    );
  },

  create: async (employee: FormData): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.post('/employee', employee).then((response) => response.data),
      {
        loading: 'Creating employee...',
        success: 'Employee created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create employee',
      },
    );
  },

  update: async (id: number, employee: FormData): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.put(`/employee/${id}`, employee).then((response) => response.data),
      {
        loading: 'Updating employee...',
        success: 'Employee updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update employee',
      },
    );
  },

  delete: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.delete(`/employee/${id}`).then((response) => response.data),
      {
        loading: 'Deleting employee...',
        success: 'Employee deleted successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to delete employee',
      },
    );
  },

  toggleStatus: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .patch(`/employee/${id}/toggle-status`)
        .then((response) => response.data),
      {
        loading: 'Toggling employee status...',
        success: 'Employee status toggled successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to toggle employee status',
      },
    );
  },

  getById: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get(`/employee/employeeData/${id}`).then((response) => response.data),
      {
        loading: 'Fetching employee...',
        success: 'Employee fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch employee',
      },
    );
  },

  search: async (
    query: string,
    company_id?: number,
  ): Promise<ApiResponse<any>> => {
    const url = company_id
      ? `/employee/search?q=${query}&company_id=${company_id}`
      : `/employee/search?q=${query}`;
    return toastWrap(
      api.get(url).then((response) => response.data),
      {
        loading: 'Searching employees...',
        success: 'Employees fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to search employees',
      },
    );
  },

  details: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get(`/employee/details/${id}`).then((response) => response.data),
      {
        loading: 'Fetching employee details...',
        success: 'Employee details fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch employee details',
      },
    );
  },
};

// =======================
// TERRITORIES API
// =======================

interface SearchableItem {
  id: number;
  name: string;
  display_name?: string;
}

export const territoriesAPI = {
  search: (query: string, company_id: number) =>
    toastWrap(
      api.get(`/territory/search?q=${query}&company_id=${company_id}`),
      {
        loading: 'Searching territories...',
        success: 'Territories fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to search territories',
      },
    ),

  getById: async (id: number): Promise<ApiResponse<SearchableItem>> => {
    return api
      .get(`/territory/${id}`)
      .then((response) => response.data)
      .catch((err) => {
        toast.error(
          err.response?.data?.message || 'Failed to fetch territories',
        );
        return err.response?.data;
      });
  },

  getAll: async (): Promise<ApiResponse<SearchableItem[]>> => {
    return toastWrap(
      api.get('/territories').then((response) => response.data),
      {
        loading: 'Fetching territories...',
        success: 'Territories fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch territories',
      },
    );
  },

  getByIds: async (ids: number): Promise<ApiResponse<SearchableItem[]>> => {
    return api
      .get('/territories/by-ids', { params: { ids } })
      .then((response) => response.data)
      .catch((err) => {
        toast.error(
          err.response?.data?.message || 'Failed to fetch territories',
        );
        return err.response?.data;
      });
  },

  getByRegionId: async (
    regionId: number,
  ): Promise<ApiResponse<SearchableItem[]>> => {
    return toastWrap(
      api
        .get('/territory/by-region-id', { params: { region_id: regionId } })
        .then((response) => response.data),
      {
        loading: 'Fetching territories...',
        success: 'Territories fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch territories',
      },
    );
  },
};

// =======================
// WAREHOUSES API
// =======================
export const warehousesAPI = {
  search: (query: string, company_id: number) =>
    toastWrap(
      api.get(`/warehouses/search?q=${query}&company_id=${company_id}`),
      {
        loading: 'Searching warehouses...',
        success: 'Warehouses fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to search warehouses',
      },
    ),

  getById: async (id: number): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api.get(`/warehouses/${id}`).then((response) => response.data),
      {
        loading: 'Fetching warehouse...',
        success: 'Warehouse fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch warehouse',
      },
    );
  },

  getAll: async (): Promise<ApiResponse<SearchableItem[]>> => {
    return toastWrap(
      api.get('/warehouses/getAll').then((response) => response.data),
      {
        loading: 'Fetching warehouses...',
        success: 'Warehouses fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch warehouses',
      },
    );
  },

  getByIds: async (ids: number[]): Promise<ApiResponse<SearchableItem[]>> => {
    return toastWrap(
      api
        .get('/warehouses/by-ids', { params: { ids } })
        .then((response) => response.data),
      {
        loading: 'Fetching warehouses...',
        success: 'Warehouses fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch warehouses',
      },
    );
  },

  create: async (vehicle: FormData): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api.post('/vehicles', vehicle).then((response) => response.data),
      {
        loading: 'Creating vehicle...',
        success: 'Vehicle created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create vehicle',
      },
    );
  },

  update: async (
    id: number,
    vehicle: FormData,
  ): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api.put(`/vehicles/${id}`, vehicle).then((response) => response.data),
      {
        loading: 'Updating vehicle...',
        success: 'Vehicle updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update vehicle',
      },
    );
  },

  delete: async (id: number): Promise<ApiResponse<void>> => {
    return toastWrap(
      api.delete(`/vehicles/${id}`).then((response) => response.data),
      {
        loading: 'Deleting vehicle...',
        success: 'Vehicle deleted successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to delete vehicle',
      },
    );
  },

  toggleStatus: async (id: number): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api
        .patch(`/vehicles/${id}/toggle-status`)
        .then((response) => response.data),
      {
        loading: 'Toggling vehicle status...',
        success: 'Vehicle status toggled successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to toggle vehicle status',
      },
    );
  },
};

// =======================
// VEHICLE API
// =======================
export const vehicleAPI = {
  search: (query: string, company_id: number) =>
    toastWrap(api.get(`/vehicles/search?q=${query}&company_id=${company_id}`), {
      loading: 'Searching vehicles...',
      success: 'Vehicles fetched successfully',
      error: (err: any) =>
        err.response?.data?.message || 'Failed to search vehicles',
    }),

  getById: async (id: number): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api.get(`/vehicles/${id}`).then((response) => response.data),
      {
        loading: 'Fetching vehicle...',
        success: 'Vehicle fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch vehicle',
      },
    );
  },

  getAll: async (): Promise<ApiResponse<SearchableItem[]>> => {
    return toastWrap(
      api.get('/vehicles').then((response) => response.data),
      {
        loading: 'Fetching vehicles...',
        success: 'Vehicles fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch vehicles',
      },
    );
  },

  create: async (vehicle: FormData): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api.post('/vehicles', vehicle).then((response) => response.data),
      {
        loading: 'Creating vehicle...',
        success: 'Vehicle created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create vehicle',
      },
    );
  },

  update: async (
    id: number,
    vehicle: FormData,
  ): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api.put(`/vehicles/${id}`, vehicle).then((response) => response.data),
      {
        loading: 'Updating vehicle...',
        success: 'Vehicle updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update vehicle',
      },
    );
  },

  delete: async (id: number): Promise<ApiResponse<void>> => {
    return toastWrap(
      api.delete(`/vehicles/${id}`).then((response) => response.data),
      {
        loading: 'Deleting vehicle...',
        success: 'Vehicle deleted successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to delete vehicle',
      },
    );
  },

  toggleStatus: async (id: number): Promise<ApiResponse<SearchableItem>> => {
    return toastWrap(
      api
        .patch(`/vehicles/${id}/toggle-status`)
        .then((response) => response.data),
      {
        loading: 'Toggling vehicle status...',
        success: 'Vehicle status toggled successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to toggle vehicle status',
      },
    );
  },
};

// =======================
// DASHBOARD API
// =======================
export const dashboardAPI = {
  getStats: async () => {
    return toastWrap(
      api.get('/admin/dashboard/stats').then((response) => response.data),
      {
        loading: 'Fetching stats...',
        success: 'Stats fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch stats',
      },
    );
  },

  getSalesGraph: async (time: string) => {
    return api
      .get(
        `/admin/dashboard/sales-stats-for-graph?timeStamp=${time}&date=${new Date().toISOString()}`,
      )
      .then((response) => response.data);
  },
  getUsersGraph: async () => {
    return api
      .get(`/admin/dashboard/users-stats-for-graph`)
      .then((response) => response.data);
  },
};

// =======================
// MODULE API
// =======================
export const moduleAPI = {
  getAll: async (): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get('/admin/role-permissions/modules')
        .then((response) => response.data),
      {
        loading: 'Fetching modules...',
        success: 'Modules fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch modules',
      },
    );
  },

  getEmployeePermissions: async (
    employeeId: number,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .get(`/admin/role-permissions/employee/${employeeId}`)
        .then((response) => response.data),
      {
        loading: 'Fetching employee permissions...',
        success: 'Employee permissions fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch employee permissions',
      },
    );
  },

  updateEmployeePermissions: async (
    employeeId: number,
    permissions: any[],
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .put(`/admin/role-permissions/employee/${employeeId}`, { permissions })
        .then((response) => response.data),
      {
        loading: 'Updating employee permissions...',
        success: 'Employee permissions updated successfully',
        error: (err: any) =>
          err.response?.data?.message ||
          'Failed to update employee permissions',
      },
    );
  },
};

// =======================
// ODOO CONFIG API
// =======================
export interface OdooConfiguration {
  id: number;
  name: string;
  url: string;
  db: string;
  username: string;
  password?: string;
  created_at: string;
  updated_at: string;
}

export const odooConfigAPI = {
  create: async (data: {
    odoo_url: string;
    odoo_db: string;
    odoo_username: string;
    odoo_password: string;
  }): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/odoo-configurations', data)
        .then((response) => response.data),
      {
        loading: 'Creating Odoo configuration...',
        success: 'Odoo configuration created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create Odoo configuration',
      },
    );
  },

  getAll: async (): Promise<
    ApiResponse<{ configurations: OdooConfiguration[] }>
  > => {
    return toastWrap(
      api.get('/admin/odoo-configurations').then((response) => response.data),
      {
        loading: 'Fetching Odoo configurations...',
        success: 'Odoo configurations fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch Odoo configurations',
      },
    );
  },

  update: async (
    id: number,
    data: Partial<OdooConfiguration>,
  ): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .put(`/admin/odoo-configurations/${id}`, data)
        .then((response) => response.data),
      {
        loading: 'Updating Odoo configuration...',
        success: 'Odoo configuration updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update Odoo configuration',
      },
    );
  },

  delete: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .delete(`/admin/odoo-configurations/${id}`)
        .then((response) => response.data),
      {
        loading: 'Deleting Odoo configuration...',
        success: 'Odoo configuration deleted successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to delete Odoo configuration',
      },
    );
  },

  authorizeData: async (id: number | string): Promise<ApiResponse<any>> => {
    if (!id) {
      throw new Error('Configuration ID is required');
    }
    return toastWrap(
      api
        .get(`/admin/odoo-configurations/${id}/authorize`)
        .then((response) => response.data),
      {
        loading: 'Authorizing Odoo configuration...',
        success: 'Odoo configuration authorized successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to authorize Odoo data',
      },
    );
  },

  getAuthorizationStream(id: number | string): string {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    // Use Lambda Function URL for streaming if available
    if (STREAM_API_URL) {
      return `${STREAM_API_URL}?id=${id}`;
    }
    // Fallback to API Gateway (non-streaming)
    return `${API_URL}/admin/odoo-configurations/${id}/authorize/stream`;
  },

  getSyncStream(id: number | string) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    // Use Lambda Function URL for streaming if available
    if (STREAM_API_URL) {
      return `${STREAM_API_URL}?id=${id}`;
    }
    return `${API_URL}/admin/odoo-configurations/${id}/authorize/progress`;
  },

  checkOdooConnectivity: async (config: {
    db: string;
    password: string;
    username: string;
    url: string;
  }) => {
    return toastWrap(
      api
        .post(
          `/admin/odoo-configurations/connection-status`,
          {
            db: config.db,
            login: config.username,
            password: config.password,
            url: config.url,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then((response) => ({
          success: true,
          message: 'Successfully connected to Odoo',
          data: response.data,
        })),
      {
        loading: 'Checking Odoo connectivity...',
        success: 'Successfully connected to Odoo',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to connect to Odoo',
      },
    );
  },
};

// =======================
// NOTIFICATION API
// =======================
export const notificationAPI = {
  send: async (data: {
    title: string;
    message: string;
    url: string | null;
  }): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.post(`/notifications/admin`, data).then((response) => response.data),
      {
        loading: 'Sending notification...',
        success: 'Notification sent successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to send notification',
      },
    );
  },

  getAll: async (): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get(`/notifications/admin`).then((response) => response.data),
      {
        loading: 'Fetching notifications...',
        success: 'Notifications fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch notifications',
      },
    );
  },
};

// =======================
// ADVERTISEMENT API
// =======================
export const advertisementAPI = {
  getAll: async (): Promise<ApiResponse<ProjectsResponse>> => {
    return toastWrap(
      api.get(`/advertisement/admin`).then((response) => response.data),
      {
        loading: 'Fetching advertisement...',
        success: 'Advertisement fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch advertisement',
      },
    );
  },
  create: async (formData: any) => {
    return toastWrap(
      api
        .post('/advertisement/admin', formData)
        .then((response) => response.data),
      {
        loading: 'Creating advertisement...',
        success: 'Advertisement created successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to create advertisement',
      },
    );
  },
  update: async (id: number | string, formData: any) => {
    return toastWrap(
      api
        .put(`/advertisement/admin/${id}`, formData, {})
        .then((response) => response.data),
      {
        loading: 'Updating advertisement...',
        success: 'Advertisement updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update advertisement',
      },
    );
  },
  delete: async (id: string): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .delete(`/advertisement/admin/${id}`)
        .then((response) => response.data),
      {
        loading: 'Deleting advertisement...',
        success: 'Advertisement deleted successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to delete advertisement',
      },
    );
  },
};

// =======================
// REGION API
// =======================

export const regionAPI = {
  getAll: async (): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get(`/region`).then((response) => response.data),
      {
        loading: 'Fetching regions...',
        success: 'Regions fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch regions',
      },
    );
  },
  search: async (query: string): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get(`/region/search?q=${query}`).then((response) => response.data),
      {
        loading: 'Searching regions...',
        success: 'Regions fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to search regions',
      },
    );
  },
  getById: async (id: number): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get(`/region/${id}`).then((response) => response.data),
      {
        loading: 'Fetching region...',
        success: 'Region fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch region',
      },
    );
  },
};

// =======================
// REPORTS API
// =======================
export const reportsAPI = {
  getAll: async (params: {
    month?: string;
    year?: string;
    territoryId?: number;
    regionId?: number;
    status?: string;
  }): Promise<ApiResponse<any>> => {
    return toastWrap(
      api.get('/admin/reports', { params }).then((response) => response.data),
      {
        loading: 'Fetching reports...',
        success: 'Reports fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch reports',
      },
    );
  },
  getFilters: async (): Promise<
    ApiResponse<{ regions: any[]; territories: any[] }>
  > => {
    return toastWrap(
      api.get('/admin/reports/filters').then((response) => response.data),
      {
        loading: 'Fetching filters...',
        success: 'Filters fetched successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to fetch filters',
      },
    );
  },
  bulkResendEmails: async (
    reportIds: string[],
  ): Promise<ApiResponse<{ success: string[]; failed: any[] }>> => {
    return toastWrap(
      api
        .post('/admin/reports/bulk-resend', { reportIds })
        .then((response) => response.data),
      {
        loading: 'Resending emails...',
        success: (res) => res.message || 'Bulk resend completed',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to resend emails',
      },
    );
  },
};
export const systemSettingsAPI = {
  getSettings: async (): Promise<ApiResponse<any>> => {
    return api.get('/admin/system-settings').then((response) => response.data);
  },
  updateSettings: async (data: any): Promise<ApiResponse<any>> => {
    return toastWrap(
      api
        .post('/admin/system-settings', data)
        .then((response) => response.data),
      {
        loading: 'Updating system settings...',
        success: 'Settings updated successfully',
        error: (err: any) =>
          err.response?.data?.message || 'Failed to update settings',
      },
    );
  },
};
